import { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/16x16/edit.svg';
import { ReactComponent as RulesIcon } from '@elementary/common-ui/assets/16x16/list.svg';
import { ReactComponent as ExpandIcon } from '@elementary/common-ui/assets/16x16/expand.svg';
import { ReactComponent as CollapseIcon } from '@elementary/common-ui/assets/16x16/collapse.svg';
import { ReactComponent as LinkIcon } from '@elementary/common-ui/assets/link.svg';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ReactComponent as HistoryIcon } from '@elementary/common-ui/assets/16x16/history.svg';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/16x16/arrow-down.svg';
import styled from 'styled-components';
import { AGENT_SESSIONS } from '../../../constants/queryKeys';
import { Flex, FlexSpace } from '../../Flex';
import IconButton from '../../IconButton';
import { fetchSessionHistory } from '../../../services/api';
import Menu from '../../Menu';
import { localizeDate } from '../../../utils/datetime';
import { capitalize } from '../../../utils/common';
import { HISTORY_PAGE_SIZE, SIDE_PANEL_WIDTH } from '../constants';
import { HistoryData } from '../hooks/useChatHistory';
import { ReactComponent as CloseIcon } from '../../../assets/close-2.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../assets/arrow-left.svg';
import { ReactComponent as ExpandPanelIcon } from '../../../assets/20x20/expand-panel.svg';
import SvgIcon from '../../SvgIcon';
import ChatRowMenu from './ChatRowMenu';
import InlineTitleEdit from './InlineTitleEdit';

const CloseIconStyled = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const BackArrowButton = styled(IconButton)`
  width: 16px;
  height: 16px;
  padding: 0;
  margin-right: 8px;

  svg {
    width: 16px;
    height: 16px;
    path {
      fill: ${({ theme }) => theme.palette.common.gray2};
    }
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const TitleSection = styled(Flex)`
  align-items: center;
  gap: 8px;
  overflow: hidden;

  & .copy-link-btn {
    opacity: 0;
    transition: opacity 150ms;
  }

  &:hover .copy-link-btn {
    opacity: 1;
  }
`;

const SidePanelHeaderSection = styled(FlexSpace)`
  width: ${SIDE_PANEL_WIDTH}px;
  min-width: ${SIDE_PANEL_WIDTH}px;
  align-items: center;
  align-self: stretch;
  padding: 0 24px;
  border-left: 1px solid ${({ theme }) => theme.palette.common.gray16};
  flex-shrink: 0;
`;

type ActionButtonsProps = {
  onToggleFullWidth?: () => void;
  isFullWidth?: boolean;
  onExpand?: () => void;
  isExpanded?: boolean;
  onNewChat: () => void;
  onOpenHistory?: () => void;
  onOpenSettings?: () => void;
  onClose?: () => void;
  historyOptions?: { value: string; label: string }[];
  onHistoryChange: (val: string) => void;
  onHistoryOpen: () => void;
  onLoadMoreHistory?: () => void;
  hasMoreHistory?: boolean;
  isLoadingMoreHistory?: boolean;
};

const CopyLinkButton = ({
  activeThreadId,
  onCopyLink,
}: {
  activeThreadId?: string;
  onCopyLink?: () => void;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  if (!activeThreadId || !onCopyLink) return null;

  const handleClick = () => {
    onCopyLink();
    setIsCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Tooltip title={isCopied ? 'Copied!' : 'Copy private link'}>
      <IconButton className="copy-link-btn" size="small" onClick={handleClick}>
        <LinkIcon />
      </IconButton>
    </Tooltip>
  );
};

const ActionButtons = ({
  onToggleFullWidth,
  isFullWidth,
  onExpand,
  isExpanded,
  onNewChat,
  onOpenHistory,
  onOpenSettings,
  onClose,
  historyOptions,
  onHistoryChange,
  onHistoryOpen,
  onLoadMoreHistory,
  hasMoreHistory,
  isLoadingMoreHistory,
}: ActionButtonsProps) => (
  <Flex sx={{ alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
    {onToggleFullWidth && !isFullWidth && (
      <Tooltip title="Expand">
        <IconButton size="small" onClick={onToggleFullWidth}>
          <SvgIcon width={18}>
            <ExpandPanelIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    )}

    {onExpand && (
      <IconButton size="small" onClick={onExpand}>
        {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
      </IconButton>
    )}

    <Tooltip title="New Chat">
      <IconButton size="small" onClick={onNewChat}>
        <EditIcon />
      </IconButton>
    </Tooltip>

    {onOpenHistory ? (
      <Tooltip title="Chat History">
        <IconButton size="small" onClick={onOpenHistory}>
          <HistoryIcon />
        </IconButton>
      </Tooltip>
    ) : (
      <Menu
        sx={{ width: '300px' }}
        menuButton={
          <IconButton size="small">
            <HistoryIcon />
          </IconButton>
        }
        menuTitle="Chat History"
        tooltip="Chat History"
        options={historyOptions}
        onChange={onHistoryChange}
        onOpen={onHistoryOpen}
        onLoadMore={onLoadMoreHistory}
        hasMore={hasMoreHistory}
        isLoadingMore={isLoadingMoreHistory}
        showSearch
        closeOnChange
      />
    )}

    {onOpenSettings && (
      <Tooltip title="Chat Rules">
        <IconButton size="small" onClick={onOpenSettings}>
          <RulesIcon />
        </IconButton>
      </Tooltip>
    )}

    {onClose && (
      <Tooltip title="Close Panel">
        <IconButton onClick={onClose}>
          <CloseIconStyled />
        </IconButton>
      </Tooltip>
    )}
  </Flex>
);

const SIDE_PANEL_TITLES = {
  history: 'Sessions',
  settings: 'Rules',
} as const;

type Props = {
  title?: string;
  onNewChat: () => void;
  onSelectHistory?: (data: HistoryData) => void;
  onOpenSettings?: () => void;
  onOpenHistory?: () => void;
  onClose?: () => void;
  onExpand?: () => void;
  isExpanded?: boolean;
  onToggleFullWidth?: () => void;
  isFullWidth?: boolean;
  onBack?: () => void;
  backTooltip?: string;
  activeSidePanel?: 'settings' | 'history' | null;
  activeThreadId?: string;
  onCopyLink?: () => void;
  onThreadLoadStart?: () => void;
};

const AiChatTitle = ({
  title,
  onNewChat,
  onSelectHistory,
  onOpenSettings,
  onOpenHistory,
  onClose,
  onExpand,
  isExpanded,
  onToggleFullWidth,
  isFullWidth,
  onBack,
  backTooltip,
  activeSidePanel,
  activeThreadId,
  onCopyLink,
  onThreadLoadStart,
}: Props) => {
  const sessionQuery = useInfiniteQuery({
    queryKey: [AGENT_SESSIONS],
    queryFn: ({ pageParam = 0 }) =>
      fetchSessionHistory(HISTORY_PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.sessions.length,
        0,
      );
      return lastPage.sessions.length === HISTORY_PAGE_SIZE
        ? loadedCount
        : undefined;
    },
  });

  const {
    refetch: refetchSessionHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = sessionQuery;

  const handleHistoryChange = (val: string) => {
    onThreadLoadStart?.();
    onSelectHistory?.({ threadId: val, messages: [] });
  };

  const historyOptions = useMemo(
    () =>
      sessionQuery.data?.pages
        .flatMap((page) => page.sessions)
        .map((session) => ({
          value: session.session_id,
          label: `${capitalize(localizeDate(session.created_at).fromNow())} - ${
            session.title?.trim() || 'Untitled chat'
          }`,
        })),
    [sessionQuery.data],
  );

  const activeSession = useMemo(() => {
    if (!activeThreadId) return undefined;
    return sessionQuery.data?.pages
      .flatMap((page) => page.sessions)
      .find((session) => session.session_id === activeThreadId);
  }, [activeThreadId, sessionQuery.data]);

  const activeTitle = activeSession?.title?.trim();
  const displayTitle = activeTitle || title;
  const canRename = !!activeThreadId && !!activeTitle;

  const [isEditingTitle, setIsEditingTitle] = useState(false);

  useEffect(() => {
    setIsEditingTitle(false);
  }, [activeThreadId]);

  const sidePanelTitle = activeSidePanel
    ? SIDE_PANEL_TITLES[activeSidePanel]
    : undefined;

  const actionButtons = (
    <ActionButtons
      onToggleFullWidth={onToggleFullWidth}
      isFullWidth={isFullWidth}
      onExpand={onExpand}
      isExpanded={isExpanded}
      onNewChat={onNewChat}
      onOpenHistory={onOpenHistory}
      onOpenSettings={onOpenSettings}
      onClose={onClose}
      historyOptions={historyOptions}
      onHistoryChange={handleHistoryChange}
      onHistoryOpen={() => refetchSessionHistory()}
      onLoadMoreHistory={() => fetchNextPage()}
      hasMoreHistory={hasNextPage}
      isLoadingMoreHistory={isFetchingNextPage}
    />
  );

  return (
    <FlexSpace
      sx={{
        width: '100%',
        mr: onClose ? 0 : 1,
        ...(isFullWidth && { alignItems: 'stretch' }),
      }}
    >
      <TitleSection>
        {onBack && (
          <Tooltip title={backTooltip || 'Back'}>
            <BackArrowButton onClick={onBack}>
              <ArrowLeftIcon />
            </BackArrowButton>
          </Tooltip>
        )}
        {isFullWidth && onToggleFullWidth && (
          <Tooltip title="Collapse">
            <IconButton size="small" onClick={onToggleFullWidth}>
              <SvgIcon width={18}>
                <ExpandPanelIcon style={{ transform: 'scaleX(-1)' }} />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        )}
        {isEditingTitle && canRename && activeThreadId ? (
          <InlineTitleEdit
            sessionId={activeThreadId}
            currentTitle={activeTitle ?? ''}
            onDone={() => setIsEditingTitle(false)}
          />
        ) : (
          <>
            <Typography
              sx={{
                color: 'common.black',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayTitle}
            </Typography>
            {activeThreadId && (
              <ChatRowMenu
                sessionId={activeThreadId}
                isPinned={!!activeSession?.pinned_at}
                canRename={canRename}
                onRename={() => setIsEditingTitle(true)}
                menuButton={
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    <SvgIcon width={16}>
                      <ArrowDownIcon />
                    </SvgIcon>
                  </IconButton>
                }
              />
            )}
          </>
        )}
        <CopyLinkButton
          activeThreadId={activeThreadId}
          onCopyLink={onCopyLink}
        />
      </TitleSection>

      {isFullWidth && sidePanelTitle ? (
        <SidePanelHeaderSection>
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            {sidePanelTitle}
          </Typography>
          {actionButtons}
        </SidePanelHeaderSection>
      ) : (
        actionButtons
      )}
    </FlexSpace>
  );
};

export default AiChatTitle;
